import { useCallback } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { useThemeContext } from "../context/themeContext";
import { toast } from "react-toastify";
import { FILTER_CONFIG } from "../utils/utils";
import { clearLocalSession } from "../utils/session";
import { emitAwardsFromResponse } from "../utils/awardEvents";
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const logFetches = process.env.NODE_ENV === "development";

// "standalone" once the PWA is installed and launched from the home screen,
// "browser" in a normal tab. Reported to the server on the auth check so the
// install rate can be measured.
function getDisplayMode() {
  return window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone
    ? "standalone"
    : "browser";
}

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

// Active Track filters as request params ("<paramKey>_filter"), as the
// backend's parse_filter_args reads them
function getFilterParams(filters) {
  return (filters ?? []).reduce((acc, filter) => {
    const config = FILTER_CONFIG.find((c) => c.type === filter.type);
    if (config) acc[`${config.paramKey}_filter`] = config.serialize(filter.value);
    return acc;
  }, {});
}

// Single-flight token refresh, shared across every useApi() instance.
//
// On page load many requests fire in parallel; if the access token has expired
// they all 401 at once. Without coordination each one would POST /token/refresh
// independently, and those concurrent rotations clobber each other's cookies —
// causing spurious failures and (on a dead session) multiple redirects. The
// module-level latch collapses a burst of 401s to one refresh and one outcome.
// It must live at module scope because apiRequest is memoised per hook instance.
let refreshPromise = null;

function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(
        `${API_BASE_URL}/token/refresh`,
        {},
        {
          withCredentials: true,
          headers: { "X-CSRF-TOKEN": getCookie("csrf_refresh_token") },
        },
      )
      // Clear the latch once settled so a later genuine expiry can refresh again.
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

export default function useApi() {
  const { setMode } = useThemeContext();

  const apiRequest = useCallback(async (url, options) => {
    const makeRequest = async () => {
      const response = await axios(url, {
        ...options,
        headers: {
          "X-CSRF-TOKEN": getCookie("csrf_access_token"),
          ...options.headers,
        },
        withCredentials: true,
      });
      // Any write can unlock an achievement; the server says so in a header,
      // read here once for every call (see utils/awardEvents.js)
      emitAwardsFromResponse(response);
      return response;
    };

    try {
      let response = await makeRequest();
      return response;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        try {
          await refreshAccessToken();
          console.log("Access token refreshed");
          return await makeRequest();
        } catch (refreshError) {
          // A 401 from the refresh itself means the session is genuinely dead
          // (refresh cookie missing/expired). Recover with a clean re-login
          // rather than letting the error reach the React error boundary.
          if (refreshError.response?.status === 401) {
            // Flag it so the global throwOnError lets this one already-handled
            // 401 skip the error boundary (see index.js) — without a blanket
            // "ignore all 401s" rule that would hide genuinely unexpected ones.
            refreshError.sessionExpired = true;
            clearLocalSession(
              "You have been signed out, please log in again",
              "info",
            );
          }
          // Re-throw so the query settles as a rejection (error state) rather
          // than success-with-undefined while the hard redirect takes over.
          // Network/5xx refresh failures propagate untagged, so offline handling
          // and the error boundary behave exactly as before.
          throw refreshError;
        }
      }
      throw error;
    }
  }, []);

  const fetchContentMetadata = useCallback(
    async (contentType, contentId) => {
      logFetches && console.log("fetchContentMetadata fired");
      const response = await apiRequest(
        `${API_BASE_URL}/metadata/${contentType}/${contentId}`,
        {
          method: "GET",
        },
      );
      return response.data;
    },
    [apiRequest],
  );

  const fetchContentAutocomplete = useCallback(
    async (searchInput, contentType) => {
      logFetches && console.log("fetchContentAutocomplete fired");
      const response = await apiRequest(
        `${API_BASE_URL}/content/autocomplete?q=${encodeURIComponent(
          searchInput,
        )}&type=${encodeURIComponent(contentType)}`,
        {
          method: "GET",
        },
      );
      return response.data;
    },
    [apiRequest],
  );

  const fetchContentSearch = useCallback(
    async (searchInput, contentType) => {
      logFetches && console.log("fetchContentSearch fired");
      const response = await apiRequest(
        `${API_BASE_URL}/content/search?q=${encodeURIComponent(
          searchInput,
        )}&type=${encodeURIComponent(contentType)}`,
        {
          method: "GET",
        },
      );
      return response.data;
    },
    [apiRequest],
  );

  const fetchJournal = useCallback(
    async ({
      pageParam = 1,
      perPage,
      username,
      contentType,
      sortBy,
      sortDescending,
      filters,
      searchTerm,
      region,
      groupByMonth = false,
    }) => {
      logFetches && console.log("fetchJournal fired");
      const response = await apiRequest(`${API_BASE_URL}/journal`, {
        method: "GET",
        params: {
          page: pageParam,
          per_page: perPage,
          username,
          content_type: contentType,
          sort_by: sortBy,
          sort_descending: sortDescending, // axios converts boolean parameter to string: "true" or "false"
          search_term: searchTerm,
          region,
          // Grouped by month, the response also counts each month, which
          // needs the viewer's timezone to place timestamps in months
          ...(groupByMonth && {
            group_by_month: true,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
          ...getFilterParams(filters),
        },
      });

      return {
        items: response.data.items,
        nextPage: response.data.hasMore ? pageParam + 1 : undefined,
        hasMore: response.data.hasMore,
        // Grouped by month only: { "YYYY-MM" | "none": { type: count } }
        monthCounts: response.data.month_counts ?? {},
      };
    },
    [apiRequest],
  );

  const patchEntry = useCallback(
    async (entryId, editedEntry) => {
      logFetches && console.log("patchEntry fired");
      const response = await apiRequest(`${API_BASE_URL}/journal/${entryId}`, {
        method: "PATCH",
        data: editedEntry,
      });
      return response.data;
    },
    [apiRequest],
  );

  const deleteEntry = useCallback(
    async (entryId, confirm = false) => {
      logFetches && console.log(`deleteEntry fired for entryId ${entryId}`);
      const response = await apiRequest(`${API_BASE_URL}/journal/${entryId}`, {
        method: "DELETE",
        data: { confirm },
      });
      return response.data;
    },
    [apiRequest],
  );

  const fetchJournalForContent = useCallback(
    async (contentId) => {
      logFetches &&
        console.log(
          `fetchJournalForContent fired for content id = ${contentId}`,
        );
      const response = await apiRequest(
        `${API_BASE_URL}/journalforcontent/${contentId}`,
        {
          method: "GET",
        },
      );
      return response.data;
    },
    [apiRequest],
  );

  const fetchTVJournalSummary = useCallback(
    async (contentId) => {
      logFetches &&
        console.log(
          `fetchTVJournalSummary fired for content id = ${contentId}`,
        );
      const response = await apiRequest(
        `${API_BASE_URL}/journal-tv-summary/${contentId}`,
        {
          method: "GET",
        },
      );
      return response.data;
    },
    [apiRequest],
  );

  // Batched form of fetchJournalForContent for multiple content ids in one
  // request (e.g. a TV series plus all its seasons) — used via journalBatcher.js
  const fetchBatchJournalForContent = useCallback(
    async (contentIds) => {
      logFetches && console.log(`fetchBatchJournalForContent fired`);
      const response = await apiRequest(
        `${API_BASE_URL}/journalforcontent/batch`,
        {
          method: "POST",
          data: { content_ids: contentIds },
        },
      );
      return response.data;
    },
    [apiRequest],
  );

  // Status endpoints

  const fetchStatuses = useCallback(
    async ({
      pageParam = 1,
      perPage,
      username,
      contentType,
      status,
      sortBy,
      sortDescending,
      filters,
      searchTerm,
      region,
      groupByMonth = false,
    }) => {
      logFetches && console.log(`fetchStatuses fired`);
      const response = await apiRequest(`${API_BASE_URL}/statuses`, {
        method: "GET",
        params: {
          page: pageParam,
          per_page: perPage,
          content_type: contentType,
          status,
          username,
          sort_by: sortBy,
          sort_descending: sortDescending, // axios converts boolean parameter to string: "true" or "false"
          search_term: searchTerm,
          region,
          // Grouped by month, the response also counts each month, which
          // needs the viewer's timezone to place timestamps in months
          ...(groupByMonth && {
            group_by_month: true,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
          ...getFilterParams(filters),
        },
      });

      return {
        items: response.data.items,
        nextPage: response.data.hasMore ? pageParam + 1 : undefined,
        hasMore: response.data.hasMore,
        total: response.data.total,
        // Grouped by month only: { "YYYY-MM": { type: count } }
        monthCounts: response.data.month_counts ?? {},
      };
    },
    [apiRequest],
  );

  // The Track tab's summary strip: counts per type as filtered and in total,
  // plus one stat for the tab (see get_library_summary)
  const fetchLibrarySummary = useCallback(
    async ({ username, contentType, status, filters, searchTerm, region }) => {
      logFetches && console.log("fetchLibrarySummary fired");
      const response = await apiRequest(`${API_BASE_URL}/library/summary`, {
        method: "GET",
        params: {
          username,
          content_type: contentType,
          status,
          search_term: searchTerm,
          region,
          // "Started this month" means the viewer's own calendar month
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ...getFilterParams(filters),
        },
      });
      return response.data;
    },
    [apiRequest],
  );

  const addStatus = useCallback(
    async ({ contentId, status, createJournalEntry, fromShortcut = false }) => {
      logFetches &&
        console.log(
          `addStatus fired with content ID ${contentId}, status ${status}`,
        );
      const data = {
        content_id: contentId,
        status: status,
        create_journal_entry: createJournalEntry,
        from_shortcut: fromShortcut,
        // The device's local today, stored as-is on any entry this creates —
        // the backend can't know the user's timezone (see resolve_entry_date).
        date: dayjs().format("YYYY-MM-DD"),
      };
      const response = await apiRequest(`${API_BASE_URL}/statuses`, {
        method: "POST",
        data,
      });
      return response.data;
    },
    [apiRequest],
  );

  const editStatus = useCallback(
    async ({ contentId, status, createJournalEntry }) => {
      logFetches && console.log("editStatus fired");
      const response = await apiRequest(`${API_BASE_URL}/status/${contentId}`, {
        method: "PUT",
        // date: same local today as addStatus, for any entry this creates
        data: { status, createJournalEntry, date: dayjs().format("YYYY-MM-DD") },
      });
      return response.data;
    },
    [apiRequest],
  );

  const flagContent = useCallback(
    async (data) => {
      logFetches && console.log("flagContent fired");
      const response = await apiRequest(`${API_BASE_URL}/content/flag`, {
        method: "POST",
        data: data,
      });
      return response.data;
    },
    [apiRequest],
  );

  const deleteStatus = useCallback(
    async (contentId, confirm) => {
      logFetches &&
        console.log(`deleteStatus fired for contentId ${contentId}`);
      const response = await apiRequest(`${API_BASE_URL}/status/${contentId}`, {
        method: "DELETE",
        data: { confirm },
      });
      return response.data;
    },
    [apiRequest],
  );

  const addContent = useCallback(
    async ({
      contentType,
      identifier,
      title = null,
      person = null,
      publishedDate = null,
    }) => {
      try {
        logFetches &&
          console.log(
            `addContent fired with content type ${contentType} and identifier ${identifier}`,
          );
        const response = await apiRequest(`${API_BASE_URL}/content/add`, {
          method: "POST",
          data: {
            content_type: contentType,
            identifier,
            title,
            person,
            published_date: publishedDate,
          },
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    [apiRequest],
  );

  /**
   * Fetch the signed-in user's own collections (view="mine") or saved collections
   * (view="saved"). When username is provided, fetches that user's public collections.
   */
  const fetchUserLists = useCallback(
    async ({
      view = "mine",
      sortDescending = true,
      sortBy = "updated",
      searchTerm = "",
      pageParam = 1,
      perPage = 5,
      contentType = "all",
      username = null,
      maxItems = null, // caps items per list (profile collections carousel)
    } = {}) => {
      logFetches && console.log(`fetchUserLists fired (view=${view})`);
      const params = new URLSearchParams();
      params.append("view", view);
      params.append("sort_descending", sortDescending);
      params.append("sort_by", sortBy);
      params.append("search_term", searchTerm);
      params.append("page", pageParam);
      params.append("per_page", perPage);
      params.append("content_type", contentType);
      if (username) params.append("username", username);
      if (maxItems) params.append("max_items", maxItems);
      const response = await apiRequest(
        `${API_BASE_URL}/user-lists?${params.toString()}`,
        { method: "GET" },
      );
      return {
        items: response.data.items,
        nextPage: response.data.hasMore ? pageParam + 1 : undefined,
        hasMore: response.data.hasMore,
      };
    },
    [apiRequest],
  );

  const fetchList = useCallback(
    async (listId) => {
      try {
        logFetches && console.log(`fetchList fired`);
        const response = await apiRequest(`${API_BASE_URL}/lists/${listId}`, {
          method: "GET",
        });
        return response.data;
      } catch (error) {
        console.error("fetchList error: ", error);
        throw error;
      }
    },
    [apiRequest],
  );

  /**
   * Fetch canonical lists filtered by a CNF tag filter for explore sections.
   * tagFilter: AND-of-ORs, e.g. [[["kind","best-of"]],[["era","21st-century"],["era","all-time"]]]
   * maxItems: cap items per list to the posters shown (default 5, e.g. CuratePreview);
   *   the response still carries the true item_count and full-list finished_count.
   * minItems: when set, excludes lists with fewer items — used by featured carousels
   *   (Explore landing page, homepage preview) so sparse collections don't make weak
   *   covers. Left unset by the "see all" detail pages, which show every collection.
   * sortBy: backend sort key — "updated" (default) or "year_content_type" for year-grouped sections.
   * Returns { items, nextPage, hasMore } for use with useQuery (preview) or useInfiniteQuery (section pages).
   */
  const fetchExploreCollections = useCallback(
    async ({
      tagFilter,
      contentType = "all",
      searchTerm = "",
      pageParam = 1,
      perPage = 5,
      maxItems = 5,
      minItems,
      sortBy = "updated",
    } = {}) => {
      logFetches && console.log(`fetchExploreCollections fired`);
      const params = new URLSearchParams();
      params.append("page", pageParam);
      params.append("per_page", perPage);
      params.append("content_type", contentType);
      params.append("search_term", searchTerm);
      params.append("max_items", maxItems);
      params.append("sort_by", sortBy);
      params.append("sort_descending", true);
      if (minItems !== undefined) {
        params.append("min_items", minItems);
      }
      if (tagFilter) {
        params.append("tags", JSON.stringify(tagFilter));
      }
      const response = await apiRequest(
        `${API_BASE_URL}/canonical-lists?${params.toString()}`,
        { method: "GET" },
      );
      return {
        items: response.data.items,
        nextPage: response.data.hasMore ? pageParam + 1 : undefined,
        hasMore: response.data.hasMore,
      };
    },
    [apiRequest],
  );

  /**
   * Fetch the distinct tags of `kind` present on canonical lists matching a content
   * type + CNF tag filter — e.g. which recommender roles have at least one book
   * collection. Each tag includes a `collection_count` so callers can drop facets
   * too thin to be worth a filter pill. Used to build filter pills that never show
   * empty results.
   */
  const fetchExploreCollectionFacets = useCallback(
    async ({ kind, tagFilter, contentType = "all" }) => {
      logFetches && console.log(`fetchExploreCollectionFacets fired`);
      const params = new URLSearchParams();
      params.append("kind", kind);
      params.append("content_type", contentType);
      if (tagFilter) {
        params.append("tags", JSON.stringify(tagFilter));
      }
      const response = await apiRequest(
        `${API_BASE_URL}/canonical-list-facets?${params.toString()}`,
        { method: "GET" },
      );
      return response.data.tags;
    },
    [apiRequest],
  );

  /**
   * Fetch a user's profile list by content type, profile_kind slug, and username.
   * profileKind is a PROFILE_KIND slug from listSchema.js (in practice TOP5_ALLTIME;
   * standouts have their own endpoints).
   */
  const fetchProfileList = useCallback(
    async (content_type, profileKind, username = null) => {
      logFetches && console.log(`fetchProfileList fired`);
      const response = await apiRequest(`${API_BASE_URL}/profile-list`, {
        method: "GET",
        params: {
          content_type,
          profile_kind: profileKind,
          ...(username && { username }),
        },
      });
      return response.data;
    },
    [apiRequest],
  );

  // Standouts calls all carry `year`: the user's local calendar year, which can
  // differ from the server's UTC year around New Year (see services/standouts.py).

  /** A user's standouts row for one content type. */
  const fetchStandouts = useCallback(
    async (contentType, username, year) => {
      logFetches && console.log(`fetchStandouts fired`);
      const response = await apiRequest(`${API_BASE_URL}/standouts`, {
        method: "GET",
        params: { content_type: contentType, username, year },
      });
      return response.data;
    },
    [apiRequest],
  );

  /** Dashboard "Recently finished": latest finishes plus their months' stats, by your local today. */
  const fetchRecentlyFinished = useCallback(
    async (contentType) => {
      logFetches && console.log(`fetchRecentlyFinished fired`);
      const response = await apiRequest(
        `${API_BASE_URL}/dashboard/recently-finished`,
        {
          method: "GET",
          params: {
            content_type: contentType,
            date: dayjs().format("YYYY-MM-DD"),
          },
        },
      );
      return response.data;
    },
    [apiRequest],
  );

  /** Your dashboard achievement rows (generic DTOs), for one pill or "all". */
  const fetchAchievements = useCallback(
    async (contentType) => {
      logFetches && console.log(`fetchAchievements fired`);
      const response = await apiRequest(
        `${API_BASE_URL}/dashboard/achievements`,
        { method: "GET", params: { contentType } },
      );
      return response.data;
    },
    [apiRequest],
  );

  /** Records that these achievements' NEW rows have been seen on the dashboard. */
  const markAchievementsSeen = useCallback(
    async (keys) => {
      logFetches && console.log(`markAchievementsSeen fired`);
      await apiRequest(`${API_BASE_URL}/dashboard/achievements/seen`, {
        method: "POST",
        data: { keys },
      });
    },
    [apiRequest],
  );

  /** Skips a Getting started step; responds with the refreshed rows. */
  const skipAchievementStep = useCallback(
    async ({ stepKey, contentType }) => {
      logFetches && console.log(`skipAchievementStep fired`);
      const response = await apiRequest(
        `${API_BASE_URL}/dashboard/achievements/steps/${stepKey}/skip`,
        { method: "POST", params: { contentType } },
      );
      return response.data;
    },
    [apiRequest],
  );

  /** Standout state for one item (per season for a series) — add-to-collection dropdown. */
  const fetchStandoutStatus = useCallback(
    async (contentId, year) => {
      logFetches && console.log(`fetchStandoutStatus fired`);
      const response = await apiRequest(
        `${API_BASE_URL}/standouts/status/${contentId}`,
        { method: "GET", params: { year } },
      );
      return response.data;
    },
    [apiRequest],
  );

  /** Items with a finish dated this year that aren't standouts yet — profile picker. */
  const fetchStandoutCandidates = useCallback(
    async (contentType, year) => {
      logFetches && console.log(`fetchStandoutCandidates fired`);
      const response = await apiRequest(
        `${API_BASE_URL}/standouts/finished-this-year`,
        { method: "GET", params: { content_type: contentType, year } },
      );
      return response.data;
    },
    [apiRequest],
  );

  /** finishDate ("YYYY-MM-DD") is required when the item has no finish dated this year. */
  const addStandout = useCallback(
    async ({ contentId, year, finishDate = null }) => {
      logFetches && console.log(`addStandout fired`);
      const response = await apiRequest(`${API_BASE_URL}/standouts`, {
        method: "POST",
        data: { contentId, year, ...(finishDate && { finishDate }) },
      });
      return response.data;
    },
    [apiRequest],
  );

  const removeStandout = useCallback(
    async ({ contentId, year }) => {
      logFetches && console.log(`removeStandout fired`);
      const response = await apiRequest(`${API_BASE_URL}/standouts/${contentId}`, {
        method: "DELETE",
        params: { year },
      });
      return response.data;
    },
    [apiRequest],
  );

  const addItemToList = useCallback(
    async ({ listId, contentId }) => {
      logFetches && console.log(`addItemToList fired`);
      const response = await apiRequest(`${API_BASE_URL}/lists/${listId}`, {
        method: "POST",
        data: { contentId },
      });
      return response.data;
    },
    [apiRequest],
  );

  const removeItemFromList = useCallback(
    async ({ listId, contentId }) => {
      logFetches && console.log(`removeItemFromList fired`);
      const response = await apiRequest(
        `${API_BASE_URL}/lists/${listId}/content/${contentId}`,
        {
          method: "DELETE",
        },
      );
      return response.data;
    },
    [apiRequest],
  );

  const replaceItemInList = useCallback(
    async ({ listId, oldContentId, newContentId }) => {
      logFetches && console.log(`replaceItemInList fired`);
      const response = await apiRequest(
        `${API_BASE_URL}/lists/${listId}/content/${oldContentId}`,
        {
          method: "PUT",
          data: { newContentId },
        },
      );
      return response.data;
    },
    [apiRequest],
  );

  const reorderItemsInList = useCallback(
    async ({ listId, items }) => {
      logFetches &&
        console.log(`reorderItemsInList fired for list ID: ${listId}`);
      const response = await apiRequest(`${API_BASE_URL}/lists/${listId}`, {
        method: "PUT",
        data: { items },
      });
      return response.data;
    },
    [apiRequest],
  );

  const editListDetails = useCallback(
    async ({
      listId,
      title,
      description,
      isRanked,
      size,
      origin,
      visibility,
      tagIds,
    }) => {
      logFetches && console.log(`editListDetails fired for list ID: ${listId}`);
      const response = await apiRequest(`${API_BASE_URL}/lists/${listId}`, {
        method: "PUT",
        // tagIds and origin included only when provided; undefined keys are omitted by JSON.stringify
        data: {
          title,
          description,
          isRanked,
          size,
          origin,
          visibility,
          tagIds,
        },
      });
      return response.data;
    },
    [apiRequest],
  );

  const createList = useCallback(
    async (newList, contentIdToAdd = null) => {
      logFetches && console.log(`createList fired`);
      const payload = {
        ...newList,
        ...(contentIdToAdd && { contentIdToAdd }),
      };
      const response = await apiRequest(`${API_BASE_URL}/lists`, {
        method: "POST",
        data: payload,
      });
      return response.data;
    },
    [apiRequest],
  );

  /** Fetch all tags grouped by kind. Used to populate the admin tag picker. */
  const getListTags = useCallback(async () => {
    logFetches && console.log("getListTags fired");
    const response = await apiRequest(`${API_BASE_URL}/lists/tags`, {
      method: "GET",
    });
    return response.data;
  }, [apiRequest]);

  /** Create a new tag (admin-only). Returns existing tag when slug already exists. */
  const createListTag = useCallback(
    async ({ displayName, kind, slug }) => {
      logFetches && console.log("createListTag fired");
      const response = await apiRequest(`${API_BASE_URL}/lists/tags`, {
        method: "POST",
        data: { displayName, kind, slug },
      });
      return response.data;
    },
    [apiRequest],
  );

  const deleteList = useCallback(
    async (listId) => {
      logFetches && console.log("deleteList fired");
      const response = await apiRequest(`${API_BASE_URL}/lists/${listId}`, {
        method: "DELETE",
      });
      return response.data;
    },
    [apiRequest],
  );

  const saveList = useCallback(
    async (listId) => {
      const response = await apiRequest(
        `${API_BASE_URL}/lists/${listId}/save`,
        { method: "POST" },
      );
      return response.data;
    },
    [apiRequest],
  );

  const unsaveList = useCallback(
    async (listId) => {
      const response = await apiRequest(
        `${API_BASE_URL}/lists/${listId}/save`,
        { method: "DELETE" },
      );
      return response.data;
    },
    [apiRequest],
  );

  const fetchActivityFeed = useCallback(
    async ({
      contentType,
      pageParam = 1,
      perPage = 10,
      scope = "following",
      preview,
    }) => {
      logFetches && console.log("fetchActivityFeed fired");
      const response = await apiRequest(`${API_BASE_URL}/feed`, {
        method: "GET",
        params: {
          contentType,
          page: pageParam,
          per_page: perPage,
          scope,
          // Preview cap (one card per author) for the homepage carousel; omitted otherwise.
          ...(preview ? { preview } : {}),
        },
      });
      return {
        items: response.data.items,
        nextPage: response.data.has_more ? pageParam + 1 : undefined,
        hasMore: response.data.hasMore,
        noFollowers: response.data.no_followers,
      };
    },
    [apiRequest],
  );

  const checkUsernameAvailability = useCallback(
    async (username) => {
      logFetches && console.log("getUserByUsername fired");
      const response = await apiRequest(`${API_BASE_URL}/check-username`, {
        method: "GET",
        params: {
          username: username,
        },
      });
      return response.data.isAvailable;
    },
    [apiRequest],
  );

  const editProfile = useCallback(
    async (data) => {
      logFetches && console.log("editProfile fired");
      const response = await apiRequest(`${API_BASE_URL}/edit-profile`, {
        method: "PUT",
        data,
      });
      return response.data;
    },
    [apiRequest],
  );

  const presignProfilePicture = useCallback(
    async (contentType) => {
      logFetches && console.log("presignProfilePicture fired");
      const response = await apiRequest(
        `${API_BASE_URL}/user/profile-picture/presign`,
        {
          method: "POST",
          data: { contentType },
        },
      );
      return response.data;
    },
    [apiRequest],
  );

  const confirmProfilePicture = useCallback(
    async (filename) => {
      logFetches && console.log("confirmProfilePicture fired");
      const response = await apiRequest(
        `${API_BASE_URL}/user/profile-picture/confirm`,
        {
          method: "POST",
          data: { filename },
        },
      );
      return response.data;
    },
    [apiRequest],
  );

  // Uploads straight to S3, not through the backend, so this deliberately bypasses
  // apiRequest: S3 doesn't return Access-Control-Allow-Credentials, so a credentialed
  // request would be blocked by the browser, and no CSRF/auth headers are relevant here
  // — the presigned URL itself is the authorization.
  const uploadAvatarToCdn = useCallback(
    async (uploadUrl, blob, { contentType, cacheControl }) => {
      logFetches && console.log("uploadAvatarToCdn fired");
      await axios.put(uploadUrl, blob, {
        headers: {
          "Content-Type": contentType,
          "Cache-Control": cacheControl,
        },
        withCredentials: false,
      });
    },
    [],
  );

  const authenticateUser = useCallback(async () => {
    logFetches && console.log("authenticateUser fired");
    const response = await apiRequest(`${API_BASE_URL}/token/auth`, {
      method: "GET",
      // The server records a visit here; only the client can tell whether the
      // PWA is installed, so the install mode rides along with the auth check.
      headers: { "X-Display-Mode": getDisplayMode() },
    });
    const { userDetails } = response.data;
    const darkMode = userDetails.preferences.darkMode;
    localStorage.setItem("darkMode", darkMode ? "true" : "false");
    setMode(darkMode ? "dark" : "light");
    return response.data;
  }, [apiRequest, setMode]);

  const fetchAdminMetrics = useCallback(async () => {
    logFetches && console.log("fetchAdminMetrics fired");
    const response = await apiRequest(`${API_BASE_URL}/admin/metrics`, {
      method: "GET",
    });
    return response.data;
  }, [apiRequest]);

  const fetchAdminMetricsHistory = useCallback(
    async (days = 180) => {
      logFetches && console.log("fetchAdminMetricsHistory fired");
      const response = await apiRequest(`${API_BASE_URL}/admin/metrics/history`, {
        method: "GET",
        params: { days },
      });
      return response.data;
    },
    [apiRequest],
  );

  const fetchAdminBookCovers = useCallback(
    async (query) => {
      logFetches && console.log("fetchAdminBookCovers fired");
      const response = await apiRequest(`${API_BASE_URL}/admin/books/covers`, {
        method: "GET",
        params: { q: query },
      });
      return response.data;
    },
    [apiRequest],
  );

  const setAdminBookCover = useCallback(
    async ({ contentId, coverId }) => {
      logFetches && console.log("setAdminBookCover fired");
      const response = await apiRequest(`${API_BASE_URL}/admin/books/${contentId}/cover`, {
        method: "PUT",
        data: { cover_id: coverId },
      });
      return response.data;
    },
    [apiRequest],
  );

  const refetchAdminBookEditions = useCallback(
    async (contentId) => {
      logFetches && console.log("refetchAdminBookEditions fired");
      const response = await apiRequest(`${API_BASE_URL}/admin/books/${contentId}/editions/refetch`, {
        method: "POST",
      });
      return response.data;
    },
    [apiRequest],
  );

  const logoutUser = async () => {
    logFetches && console.log("logoutUser fired");
    const response = await apiRequest(`${API_BASE_URL}/logout`, {
      method: "POST",
    });
    return response.data;
  };

  const deleteUser = useCallback(async () => {
    logFetches && console.log("deleteUser fired");
    try {
      const response = await apiRequest(`${API_BASE_URL}/delete-account`, {
        method: "POST",
      });
      // Account is gone — tear down the local session unconditionally. No /logout
      // call: it's redundant once the account is deleted, and we must not let a
      // failed /logout block the teardown.
      clearLocalSession("Account deleted successfully");
      return response.data;
    } catch (error) {
      console.error("Error deleting user account:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete user account",
      );
    }
  }, [apiRequest]);

  const fetchUserSearch = useCallback(
    async (searchInput) => {
      logFetches && console.log("fetchUserSearch fired");
      const response = await apiRequest(
        `${API_BASE_URL}/user/search?q=${encodeURIComponent(searchInput)}`,
        {
          method: "GET",
        },
      );
      return response.data;
    },
    [apiRequest],
  );

  const fetchUserProfile = async (username) => {
    logFetches && console.log(`fetchUserProfile`);
    const response = await apiRequest(
      `${API_BASE_URL}/get-profile/${username}`,
      {
        method: "GET",
      },
    );
    return response.data;
  };

  const followUser = async (username) => {
    logFetches && console.log("followUser fired");
    const response = await apiRequest(`${API_BASE_URL}/follow/${username}`, {
      method: "POST",
    });
    return response.data;
  };

  const unfollowUser = async (username) => {
    logFetches && console.log("unfollowUser fired");
    const response = await apiRequest(`${API_BASE_URL}/unfollow/${username}`, {
      method: "DELETE",
    });
    return response.data;
  };

  // fileOrFiles is either a single File (every source but IMDb) or an array
  // of { file, type } (multi-file sources — each part is posted as
  // file_<type>, matched server-side by initiate_import's multi-file path).
  const importLibrary = async (fileOrFiles, source, options = {}) => {
    const formData = new FormData();
    if (Array.isArray(fileOrFiles)) {
      fileOrFiles.forEach(({ file, type }) => {
        formData.append(`file_${type}`, file);
      });
      logFetches &&
        console.log(
          `importLibrary fired for source: ${source}`,
          fileOrFiles.map((f) => f.file.name),
        );
    } else {
      formData.append("file", fileOrFiles);
      logFetches &&
        console.log(`importLibrary fired for source: ${source}`, fileOrFiles.name);
    }
    formData.append("source", source);
    formData.append("options", JSON.stringify(options));

    const response = await apiRequest(`${API_BASE_URL}/import`, {
      method: "POST",
      data: formData,
    });

    const parsed =
      typeof response.data === "string"
        ? JSON.parse(response.data)
        : response.data;

    return parsed;
  };

  const joinWaitlist = async (email) => {
    logFetches && console.log("joinWaitlist fired");
    const response = await apiRequest(`${API_BASE_URL}/join-waitlist`, {
      method: "POST",
      data: { email },
    });
    return response.data;
  };

  const fetchImportStatus = async (source) => {
    logFetches && console.log(`fetchImportStatus fired`);
    const response = await apiRequest(
      `${API_BASE_URL}/import/status?source=${source}`,
      {
        method: "GET",
      },
    );
    return response.data;
  };

  const fetchImportSummary = async (source) => {
    logFetches && console.log(`fetchImportSummary fired`);
    const response = await apiRequest(
      `${API_BASE_URL}/import/summary?source=${source}`,
      {
        method: "GET",
      },
    );
    if (response.status === 204) return null;
    return response.data;
  };

  const fetchSeasons = async (contentId) => {
    logFetches && console.log(`fetchSeasons fired`);
    const response = await apiRequest(
      `${API_BASE_URL}/content/tvseasons?content_id=${contentId}`,
      {
        method: "GET",
      },
    );
    return response.data;
  };

  const fetchBatchStatuses = useCallback(
    async (contentIds) => {
      logFetches && console.log(`fetchStatusesBatch fired`);
      const response = await apiRequest(`${API_BASE_URL}/statuses/batch`, {
        method: "POST",
        data: { content_ids: contentIds },
      });
      return response.data;
    },
    [apiRequest],
  );

  const editUiState = async (payload) => {
    // payload: { key: string, value: boolean }
    logFetches && console.log("editTutorialState fired", payload);
    const response = await apiRequest(`${API_BASE_URL}/edit-ui-state`, {
      method: "PUT",
      data: payload,
    });
    return response.data;
  };

  const fetchListsForContent = useCallback(
    async (contentId) => {
      logFetches && console.log("fetchListsForContent fired");
      const response = await apiRequest(
        `${API_BASE_URL}/lists/for-content/${contentId}`,
        {
          method: "GET",
        },
      );
      return response.data;
    },
    [apiRequest],
  );

  // Powers the "Following" tab in LibraryModal — how the accounts this user
  // follows have engaged with a given content item (statuses + ratings).
  const fetchFollowingForContent = useCallback(
    async (contentId) => {
      logFetches && console.log("fetchFollowingForContent fired");
      const response = await apiRequest(
        `${API_BASE_URL}/following/for-content/${contentId}`,
        {
          method: "GET",
        },
      );
      return response.data;
    },
    [apiRequest],
  );

  // Resolves a single journal entry for any viewer — backs shared entry links
  // and track page tiles (?entry=<id>), which must resolve regardless of who
  // follows whom, or whether the viewer is signed in at all.
  const fetchEntry = useCallback(
    async (journalId) => {
      logFetches && console.log("fetchEntry fired");
      const response = await apiRequest(`${API_BASE_URL}/entry/${journalId}`, {
        method: "GET",
      });
      return response.data;
    },
    [apiRequest],
  );

  const fetchProfileStats = useCallback(
    async (contentType, username, year) => {
      logFetches && console.log("fetchProfileStats fired");
      const response = await apiRequest(`${API_BASE_URL}/content/stats`, {
        method: "GET",
        params: {
          content_type: contentType,
          username,
          year,
        },
      });
      return response.data;
    },
    [apiRequest],
  );

  const fetchWatchProviders = useCallback(
    async (region) => {
      logFetches && console.log("fetchWatchProviders fired");
      const response = await apiRequest(
        `${API_BASE_URL}/content/watch-providers`,
        {
          method: "GET",
          params: { region },
        },
      );
      return response.data;
    },
    [apiRequest],
  );

  // Options for every data-driven Track filter, keyed by FILTER_CONFIG type
  // (e.g. { Provider: [...], Language: [...] }).
  const fetchFilterOptions = useCallback(
    async ({ username, contentType }) => {
      logFetches && console.log("fetchFilterOptions fired");
      const response = await apiRequest(
        `${API_BASE_URL}/content/filter-options`,
        {
          method: "GET",
          params: {
            username,
            content_type: contentType,
          },
        },
      );
      return response.data;
    },
    [apiRequest],
  );

  const getUnreadNotificationCount = useCallback(
    async (kind = "whats_new") => {
      logFetches && console.log("getUnreadNotificationCount fired");
      const response = await apiRequest(
        `${API_BASE_URL}/notifications/unread-count`,
        { method: "GET", params: { kind } },
      );
      return response.data;
    },
    [apiRequest],
  );

  const markNotificationsRead = useCallback(
    async ({ kind }) => {
      logFetches && console.log("markNotificationsRead fired");
      const response = await apiRequest(
        `${API_BASE_URL}/notifications/mark-read`,
        { method: "POST", data: { kind } },
      );
      return response.data;
    },
    [apiRequest],
  );

  const getWhatsNewFeed = useCallback(async () => {
    logFetches && console.log("getWhatsNewFeed fired");
    const response = await apiRequest(`${API_BASE_URL}/whats-new/feed`, {
      method: "GET",
    });
    return response.data;
  }, [apiRequest]);

  return {
    fetchFilterOptions,
    fetchLibrarySummary,
    fetchWatchProviders,
    fetchContentAutocomplete,
    fetchContentMetadata,
    fetchContentSearch,
    fetchJournal,
    fetchStatuses,
    patchEntry,
    fetchJournalForContent,
    deleteEntry,
    fetchUserLists,
    fetchExploreCollections,
    fetchExploreCollectionFacets,
    fetchList,
    editStatus,
    addItemToList,
    removeItemFromList,
    replaceItemInList,
    reorderItemsInList,
    deleteList,
    createList,
    editListDetails,
    addStatus,
    addContent,
    deleteStatus,
    fetchActivityFeed,
    fetchProfileList,
    fetchStandouts,
    fetchRecentlyFinished,
    fetchAchievements,
    markAchievementsSeen,
    skipAchievementStep,
    fetchStandoutStatus,
    fetchStandoutCandidates,
    addStandout,
    removeStandout,
    checkUsernameAvailability,
    editProfile,
    presignProfilePicture,
    confirmProfilePicture,
    uploadAvatarToCdn,
    authenticateUser,
    fetchAdminMetrics,
    fetchAdminMetricsHistory,
    fetchAdminBookCovers,
    setAdminBookCover,
    refetchAdminBookEditions,
    logoutUser,
    deleteUser,
    fetchUserSearch,
    fetchUserProfile,
    followUser,
    unfollowUser,
    importLibrary,
    flagContent,
    joinWaitlist,
    fetchImportStatus,
    fetchImportSummary,
    fetchSeasons,
    fetchBatchStatuses,
    editUiState,
    fetchListsForContent,
    fetchFollowingForContent,
    fetchEntry,
    fetchProfileStats,
    fetchTVJournalSummary,
    fetchBatchJournalForContent,
    saveList,
    unsaveList,
    getUnreadNotificationCount,
    markNotificationsRead,
    getWhatsNewFeed,
    getListTags,
    createListTag,
  };
}
